
export const AboutSection = () => {
  const values = [
    {
      icon: 'ri-earth-line',
      title: 'Environmental Leadership',
      description: 'Pioneering sustainable biotechnology solutions for global environmental protection'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Scientific Innovation',
      description: 'Advancing marine biotechnology through breakthrough research and development'
    },
    {
      icon: 'ri-heart-line',
      title: 'Safety Excellence',
      description: 'Ensuring the highest standards of safety and quality in all our products'
    },
    {
      icon: 'ri-team-line',
      title: 'Global Impact',
      description: 'Creating positive change through sustainable biotechnology solutions worldwide'
    }
  ];

  const timeline = [
    {
      year: '2019',
      title: 'Company Founded',
      description: 'Established MarineBioGroup for advanced marine biotechnology research'
    },
    {
      year: '2020',
      title: 'Technology Breakthrough',
      description: 'Developed proprietary marine extraction and processing technologies'
    },
    {
      year: '2021',
      title: 'Patent Portfolio',
      description: 'Secured key patents in marine biotechnology and sustainable materials'
    },
    {
      year: '2022',
      title: 'Product Development',
      description: 'Completed development of industrial-grade bio-material products'
    },
    {
      year: '2023',
      title: 'Commercial Launch',
      description: 'Successfully launched comprehensive biotechnology solutions portfolio'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            About MarineBioGroup
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Leading the revolution in sustainable biotechnology with innovative marine-based solutions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Company Image */}
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/d5a2307ccb4f89a2384b36f7f33bdabc.jpg"
              alt="MarineBioGroup Overview"
              className="rounded-2xl shadow-2xl object-cover w-full h-96"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/10 to-transparent rounded-2xl"></div>
          </div>

          {/* Company Description */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Pioneering Sustainable Biotechnology
            </h3>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                MarineBioGroup is a leading biotechnology company specializing in the development 
                of sustainable, marine-derived solutions for industrial and consumer applications.
              </p>
              <p>
                Our cutting-edge research combines marine biology expertise with advanced 
                biotechnology to create environmentally responsible alternatives to traditional materials.
              </p>
              <p>
                Through continuous innovation and scientific excellence, we are transforming 
                industries with our breakthrough marine biotechnology platform.
              </p>
            </div>

            {/* Company Stats */}
            <div className="grid grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">5 Years</div>
                <div className="text-sm text-gray-600">Innovation Leadership</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">25+</div>
                <div className="text-sm text-gray-600">Patents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">75+</div>
                <div className="text-sm text-gray-600">Scientists</div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">Our Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${value.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {value.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">Our Journey</h3>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200"></div>
              
              {/* Timeline Items */}
              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                      <div className="bg-white rounded-lg p-6 shadow-lg">
                        <div className="text-lg font-bold text-blue-600 mb-2">{item.year}</div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                    
                    {/* Timeline Dot */}
                    <div className="relative z-10 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg"></div>
                    
                    <div className="w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
